import objectPath from 'object-path'
import { createDesignerData, loadApiToFormData } from '../Design'
import { updateCalculate } from './Calculate'
import { checkCondition, getCompareValue, getConditionKey } from './State'
import {
  checkEnableContainer,
  checkFalseFromAndOr,
  showInvisibleElementAction,
} from '../Condition'
import {
  compareTwoValue,
  getCurrentUi,
  getDateAge,
  updateUiSchema,
} from '../Data'
import { debounceCallback } from 'utility/debounceCallback.util'
import dayjs from 'dayjs'
import cloneDeep from 'clone-deep'
import {
  checkConditionApi,
  deleteData,
  fetchAfterSuccess,
  getData,
  mapResponseData,
  patchData,
  postData,
  putData,
} from '../Api'
import { createCallAPIActionList } from './CallApi.util'
import {
  isExternalApiUrl,
  ROUTE_API,
  ROUTE_HIS,
  TARGET_NEW_PAGE,
} from '../Config'
import {
  calAgeToDate,
  DEFAULT_DATE,
  DEFAULT_DATETIME,
  DEFAULT_TIME,
  getDateTimeFormValue,
} from '../Date'
import { onSyncData } from './action/SyncData'
import Swal from 'sweetalert2'
import FeatherIcon from 'feather-icons-react'
import { getFormGroup } from '../FormGroup'
import {
  clearFormStorage,
  getResetKeyList,
  resetKey,
} from 'designer/component/element/button/ButtonAction'
import { getFormGroupResetKeyList } from 'designer/component/element/button/ResetFormGroup'
import { hasRules, onCheckRules } from 'designer/component/rules/Action'
import {
  createFetchOptionStatus,
  shouldUpdateFetchOptionStatus,
} from './FetchOption.util'
import {
  getDesignerRequestVersion,
  isDesignerRequestVersionActive,
} from '../RequestTimeout'
import {
  getConditionActionTrigger,
  publishConditionActionDebug,
  traceConditionAction,
} from './Debug'

const isPromise = (result) => typeof result?.then === 'function'

const isConditionCallApiAction = (action = {}) => action.state === 'callAPI'

const isConditionCallApiWaitMode = (action = {}) => {
  if (!isConditionCallApiAction(action)) return false

  return !['Background', 'No Wait', 'NoWait'].includes(action.mode)
}

const runConditionActions = ({
  actions = [],
  formData,
  elementAction,
  designContext,
  status,
  isPass,
  uiKey,
  startIndex = 0,
  isCommit = false,
  pendingActions = [],
}) => {
  const requestVersion = getDesignerRequestVersion()
  for (let index = startIndex; index < actions.length; index++) {
    if (!isDesignerRequestVersionActive(requestVersion)) return elementAction
    let action = actions[index]
    let { state } = action
    if (!state) continue

    let isWaitCallApi = isConditionCallApiWaitMode(action)
    traceConditionAction(designContext, {
      type: 'action',
      trigger: getConditionActionTrigger(status),
      uiKey,
      actionKey: action.key,
      actionState: state,
      mode: action.mode,
      waitMode: isWaitCallApi ? 'Wait' : action.mode || 'NoWait',
      isPass,
    })
    if (isWaitCallApi && pendingActions.length > 0) {
      const currentFormData = formData
      const currentElementAction = elementAction
      Promise.allSettled(pendingActions)
        .then(() => {
          if (!isDesignerRequestVersionActive(requestVersion)) return
          runConditionActions({
            actions,
            formData: currentFormData,
            elementAction: currentElementAction,
            designContext,
            status,
            isPass,
            uiKey,
            startIndex: index,
            isCommit: true,
            pendingActions: [],
          })
        })
        .catch((error) => {
          console.error('[ConditionAction] wait action error', error)
        })
      return elementAction
    }

    let resultData = callAction({
      action,
      formData,
      elementAction,
      designContext,
      status,
      isPass,
      uiKey,
    })
    if (isPromise(resultData)) {
      if (isConditionCallApiAction(action) && !isWaitCallApi) {
        resultData.catch((error) => {
          console.error('[ConditionAction] run action error', error)
        })
        pendingActions.push(resultData)
        continue
      }

      const currentFormData = formData
      const currentElementAction = elementAction
      resultData
        .then((data) => {
          if (!isDesignerRequestVersionActive(requestVersion)) return
          runConditionActions({
            actions,
            formData: data?.formData ?? currentFormData,
            elementAction: data?.elementAction ?? currentElementAction,
            designContext,
            status,
            isPass,
            uiKey,
            startIndex: index + 1,
            isCommit: true,
            pendingActions: [],
          })
        })
        .catch((error) => {
          console.error('[ConditionAction] run action error', error)
        })
      return elementAction
    }

    formData = resultData?.formData ?? formData
    elementAction = resultData?.elementAction ?? elementAction
  }

  if (isCommit && designContext.setElementAction) {
    designContext.setElementAction(elementAction)
  }

  return elementAction
}

const checkIfElseCondition = (itemCondition, elementAction) => {
  let { item, designContext, data, status, uiKey } = itemCondition
  let { ifElse = [] } = item
  let caseIndex = -1
  for (let index = 0; index < ifElse.length; index++) {
    let item = ifElse[index]
    let { conditions = [], actions = [], connector = [] } = item
    let conditionCheck = []
    conditions.forEach((item, index) => {
      let conditionKey = item.key
      let conditionName = item.valueKey
      let conditionState = item.state
      if (!conditionKey || !conditionState) return
      let conditionValue = objectPath.get(item, [
        conditionState,
        'value',
        conditionKey,
      ])
      let checkValue = checkCondition(
        designContext,
        { conditionKey, conditionState, conditionValue, conditionName },
        { ...data, ...status },
        status,
        item,
      )

      conditionCheck.push(checkValue)
      if (objectPath.has(connector, index)) {
        let connectorValue = objectPath.get(connector, index)
        conditionCheck.push(connectorValue)
      }
    })
    let isPass
    if (conditionCheck.includes('and') && conditionCheck.includes('or')) {
      isPass = checkFalseFromAndOr(actions, isPass, '', false)
    } else if (conditionCheck.includes('and')) {
      if (conditionCheck.includes(false)) {
        isPass = false
      } else {
        isPass = true
      }
    } else if (conditionCheck.includes('or')) {
      if (conditionCheck.includes(true)) {
        isPass = true
      } else {
        isPass = false
      }
    } else {
      if (conditionCheck.includes(true)) {
        isPass = true
      } else {
        isPass = false
      }
    }
    if (caseIndex === -1 && isPass) {
      caseIndex = index
      break
    }
  }
  if (caseIndex === -1) {
    traceConditionAction(designContext, {
      type: 'condition',
      conditionType: 'ifElse',
      trigger: getConditionActionTrigger(status),
      uiKey,
      conditionKey: item.key,
      branchIndex: caseIndex,
      isPass: false,
      actionCount: 0,
    })
    return elementAction
  }
  let passCondition = ifElse[caseIndex]

  if (passCondition) {
    let isPass = true
    let { actions } = passCondition
    traceConditionAction(designContext, {
      type: 'condition',
      conditionType: 'ifElse',
      trigger: getConditionActionTrigger(status),
      uiKey,
      conditionKey: item.key,
      branchIndex: caseIndex,
      isPass,
      actionCount: actions?.length || 0,
      actionStates: (actions || []).map(({ state }) => state),
    })
    let formData = objectPath.get(data, 'formData')
    elementAction = runConditionActions({
      actions,
      formData,
      elementAction,
      designContext,
      status,
      isPass,
      uiKey,
    })
  }
  return elementAction
}

const checkDefaultCondition = (itemCondition, elementAction) => {
  let { item, designContext, data, status, uiKey } = itemCondition
  let { conditions = [], actions = [], connector = [], loadUi } = item
  let conditionCheck = []
  let isPass
  let { jsonForm } = designContext
  let formData = jsonForm.formData
  conditions.forEach((item, index) => {
    let conditionKey = item.key
    let conditionState = item.state
    if (!conditionKey || !conditionState) return
    let conditionValue = objectPath.get(item, [
      conditionState,
      'value',
      conditionKey,
    ])
    let checkValue = checkCondition(
      designContext,
      { conditionKey, conditionState, conditionValue },
      { ...data, ...status, formData },
      status,
      item,
    )
    conditionCheck.push(checkValue)
    if (objectPath.has(connector, index)) {
      let connectorValue = objectPath.get(connector, index)
      conditionCheck.push(connectorValue)
    }
  })
  if (conditionCheck.includes('and') && conditionCheck.includes('or')) {
    isPass = checkFalseFromAndOr(actions, isPass, '', false)
  } else if (conditionCheck.includes('and')) {
    if (conditionCheck.includes(false)) {
      isPass = false
    } else {
      isPass = true
    }
  } else if (conditionCheck.includes('or')) {
    if (conditionCheck.includes(true)) {
      isPass = true
    } else {
      isPass = false
    }
  } else {
    if (conditionCheck.includes(true)) {
      isPass = true
    } else {
      isPass = false
    }
  }

  traceConditionAction(designContext, {
    type: 'condition',
    conditionType: 'default',
    trigger: getConditionActionTrigger(status),
    uiKey,
    conditionKey: item.key,
    isPass,
    actionCount: actions?.length || 0,
    actionStates: (actions || []).map(({ state }) => state),
  })

  if (isPass) {
    if (loadUi) {
      onShowLoadUI(null, formData, elementAction, designContext)
    }
    elementAction = runConditionActions({
      actions,
      formData,
      elementAction,
      designContext,
      status,
      isPass,
      uiKey,
    })
  }
  return elementAction
}

export const searchCondition = (
  jsonForm,
  uiKey,
  { stateKey, actionKey, actionUiKey, returnActionKey },
) => {
  const conditions = objectPath.get(jsonForm, 'conditions') || []
  let filterCondition = conditions.filter(({ isEnabled }) => isEnabled)
  let actionKeyList = []
  if (uiKey) {
    filterCondition = conditions.filter(
      ({ conditions = [], actions = [], isIfElse, ifElse, isEnabled }) => {
        if (!isEnabled) return
        if (isIfElse) {
          let filterIfElse = ifElse.filter(
            ({ conditions = [], actions = [] }) => {
              let foundCondition = conditions.find(({ key, state }) => {
                let isPassState = stateKey ? state === stateKey : true
                if (Array.isArray(uiKey)) {
                  return uiKey.includes(key) && isPassState
                }
                return uiKey === key && isPassState
              })
              if (actionKey) {
                let foundAction = actions.find(({ key, state }) => {
                  let isPassState = actionKey ? state === actionKey : true
                  if (actionUiKey) {
                    if (Array.isArray(actionUiKey)) {
                      return actionUiKey.includes(key) && isPassState
                    }
                    return actionUiKey === key && isPassState
                  }
                  return isPassState
                })
                if (foundAction && foundCondition) {
                  actionKeyList = [...actionKeyList, ...actions]
                }
                return foundAction && foundCondition
              }
              return foundCondition
            },
          )
          return filterIfElse.length > 0
        }
        let foundCondition = conditions.find(({ key, state }) => {
          let isPassState = stateKey ? state === stateKey : true
          if (Array.isArray(uiKey)) {
            return uiKey.includes(key) && isPassState
          }
          return uiKey === key && isPassState
        })
        if (actionKey) {
          let foundAction = actions.find(({ key, state }) => {
            let isPassState = actionKey ? state === actionKey : true
            if (actionUiKey) {
              if (Array.isArray(actionUiKey)) {
                return actionUiKey.includes(key) && isPassState
              }
              return actionUiKey === key && isPassState
            }
            return isPassState
          })
          if (foundAction && foundCondition) {
            actionKeyList = [...actionKeyList, ...actions]
          }
          return foundAction && foundCondition
        }
        return foundCondition
      },
    )
  }
  if (returnActionKey) {
    return actionKeyList
  }
  return filterCondition
}

export const checkConditionList = (designContext, formData, uiKey, status) => {
  let {
    jsonForm,
    elementAction = {},
    setElementAction,
    urlParams,
    isLoadingApi,
    setIsLoadingApi,
    isUiChanged = {},
    setIsUiChanged,
  } = designContext

  let datasources = objectPath.get(designContext, '@datasource') || {}
  let response = status?.response
  let data = { formData, urlParams, datasources, response }
  let isCheckingCondition = objectPath.get(isUiChanged, 'isCheckingCondition')

  if (uiKey && isCheckingCondition === uiKey) {
    return
  }

  objectPath.set(isUiChanged, 'isCheckingCondition', uiKey)

  let {
    isCheckOnlyResponse,
    isCheckOnlyUrlParam,
    filterCondition,
    isModalCondition,
    isCheckOnlyCalculate,
    modalCondition,
    apiKey,
    isMainDynamic,
    formComponentCondition,
  } = status
  let conditionUiKeys = Array.isArray(uiKey) ? uiKey : [uiKey]
  if (Array.isArray(status.ruleUiKeys)) {
    conditionUiKeys = [...conditionUiKeys, ...status.ruleUiKeys]
  }
  conditionUiKeys = conditionUiKeys.filter((key) => key)
  let conditions = objectPath.get(jsonForm, 'conditions') || []
  if (isModalCondition && modalCondition) {
    conditions = [...conditions, ...modalCondition]
  }
  if (formComponentCondition) {
    conditions = [...conditions, ...formComponentCondition]
  }

  if (!filterCondition || filterCondition.length === 0) {
    if (isCheckOnlyResponse) {
      filterCondition = conditions.filter(
        ({ isEnabled, conditions, isIfElse, ifElse = [] }) => {
          if (!isEnabled) return
          if (isIfElse) {
            let foundIfElse = ifElse.find(({ conditions }) => {
              let isResponse = conditions.find(({ groupType, key }) => {
                let isPassGroupType = groupType === 'response'
                let isPassKey = key === apiKey || key.startsWith(apiKey)
                if (apiKey) {
                  return isPassKey && isPassGroupType
                }
                return isPassGroupType
              })
              return isResponse
            })
            return foundIfElse
          }
          if (!conditions) return
          let isResponse = conditions.find(({ groupType, key }) => {
            let isPassGroupType = groupType === 'response'
            let isPassKey = key === apiKey || key.startsWith(apiKey)
            if (apiKey) {
              return isPassKey && isPassGroupType
            }
            return isPassGroupType
          })
          return isResponse
        },
      )
    } else if (isCheckOnlyUrlParam) {
      filterCondition = conditions.filter(
        ({ isEnabled, conditions, isIfElse, ifElse = [] }) => {
          if (!isEnabled) return
          if (isIfElse) {
            let foundIfElse = ifElse.find(({ conditions }) => {
              let isResponse = conditions.find(
                ({ layout, name }) =>
                  layout === 'urlParam' ||
                  (layout === 'datasources' && name === 'QueryParam'),
              )
              return isResponse
            })
            return foundIfElse
          }
          if (!conditions) return
          let isResponse = conditions.find(
            ({ layout, name }) =>
              layout === 'urlParam' ||
              (layout === 'datasources' && name === 'QueryParam'),
          )
          return isResponse
        },
      )
    } else if (isCheckOnlyCalculate) {
      filterCondition = conditions.filter(
        ({ isEnabled, actions, isIfElse, ifElse = [] }) => {
          if (!isEnabled) return
          if (isIfElse) {
            let foundIfElse = ifElse.find(({ conditions, actions }) => {
              let isCalculate = actions.find(
                ({ state }) => state === 'isCalculate',
              )
              if (uiKey) {
                let foundCondition = conditions.find(({ key }) => {
                  if (Array.isArray(uiKey)) {
                    return uiKey.includes(key)
                  }
                  return uiKey === key
                })
                if (foundCondition) return
              }
              return isCalculate
            })
            return foundIfElse
          }
          if (!actions) return
          if (!conditions) return
          let isCalculate = actions.find(({ state }) => state === 'isCalculate')
          if (uiKey) {
            let foundCondition = conditions.find(({ key }) => {
              if (Array.isArray(uiKey)) {
                return uiKey.includes(key)
              }
              return uiKey === key
            })
            if (foundCondition) return
          }
          return isCalculate
        },
      )
    } else if (isMainDynamic) {
      filterCondition = conditions.filter(
        ({ isEnabled, conditions, isIfElse, ifElse = [] }) => {
          if (!isEnabled) return
          if (isIfElse) {
            let foundIfElse = ifElse.find(({ conditions }) => {
              let foundCondition = conditions.find(({ key }) => {
                let mapKey = jsonForm.schema.mapKey
                let fullKey = objectPath.get(mapKey, key)
                if (fullKey) {
                  let fullKeyList = fullKey.split('.')
                  return fullKeyList.includes(uiKey)
                }
                return false
              })
              return foundCondition
            })
            return foundIfElse
          }
          if (!conditions) return
          let foundCondition = conditions.find(({ key }) => {
            let mapKey = jsonForm.schema.mapKey
            let fullKey = objectPath.get(mapKey, key)
            if (fullKey) {
              let fullKeyList = fullKey.split('.')
              return fullKeyList.includes(uiKey)
            }
            return false
          })
          return foundCondition
        },
      )
    } else if (uiKey) {
      filterCondition = conditions.filter(
        ({ isEnabled, conditions, isIfElse, ifElse = [] }) => {
          if (!isEnabled) return

          if (isIfElse) {
            let foundIfElse = ifElse.find(({ conditions }) => {
              let foundCondition = conditions.find(({ key }) => {
                return conditionUiKeys.includes(key)
              })
              return foundCondition
            })
            return foundIfElse
          }
          if (!conditions) return
          let foundCondition = conditions.find(({ key }) => {
            return conditionUiKeys.includes(key)
          })
          return foundCondition
        },
      )
    } else {
      filterCondition = conditions.filter(({ isEnabled }) => isEnabled)
    }
  }

  if (filterCondition.length === 0) {
    if (objectPath.get(isUiChanged, 'isCheckingCondition') !== '') {
      objectPath.set(isUiChanged, 'isCheckingCondition', '')
      setIsUiChanged(isUiChanged)
    }
    return
  }

  filterCondition.forEach((item) => {
    let { isIfElse } = item
    let itemCondition = {
      item,
      designContext,
      data,
      uiKey,
      status,
    }
    if (isIfElse) {
      let { isMainDynamic, dynamicName, dynamicKey, dynamicData } = status
      if (isMainDynamic && dynamicData) {
        dynamicData.forEach((itemData, dynamicIndex) => {
          let itemCondition = {
            item,
            designContext,
            data: { ...data, itemData },
            uiKey,
            status: { ...status, isDynamic: true, dynamicIndex },
          }
          elementAction = checkIfElseCondition(itemCondition, elementAction)
        })
      } else {
        elementAction = checkIfElseCondition(itemCondition, elementAction)
      }
    } else {
      elementAction = checkDefaultCondition(itemCondition, elementAction)
    }
  })
  setElementAction(elementAction)
  objectPath.set(isUiChanged, 'isCheckingCondition', '')
  setIsUiChanged(isUiChanged)
  return formData
}

export const onCheckCondition = (
  designContext,
  data = null,
  uiKey = null,
  status = {
    isCheckOnlyCondition: false,
    isCheckOnlyCalculate: false,
    indexKeyList: null,
    showInvisible: false,
    isDebounce: true,
    isActive: false,
    isOnClick: false,
    isAfterSuccess: false,
    isLocked: 0,
    isSelectRow: false,
    isAddRow: false,
    isEditRow: false,
    isDeleteRow: false,
    isCheckOnlyResponse: false,
    isCheckOnlyUrlParam: false,
    isExceptApi: false,
    dataAdd: null,
    modalId: null,
    isCloseModal: false,
    isOnChange: false,
  },
) => {
  let {
    searchData,
    isPage,
    jsonForm,
    setFormData,
    isUiChanged,
    setIsUiChanged,
  } = designContext

  publishConditionActionDebug(designContext)

  status.isPage = isPage
  let designCode = status?.designCode
  let conditionData = isPage === 1 ? searchData : data ?? jsonForm.formData
  let isCheckingDesignCode = objectPath.get(isUiChanged, 'isCheckingDesignCode')
  let queryString = status?.queryString

  if (
    !status?.isOpenModal &&
    designCode &&
    objectPath.has(status, 'queryString')
  ) {
    if (
      queryString === isUiChanged?.queryString &&
      isCheckingDesignCode === designCode
    ) {
      return
    }
    objectPath.set(isUiChanged, 'queryString', queryString)
  }
  if (!status.isMainDynamic && status.isDynamic) {
    objectPath.set(
      isUiChanged,
      `isOnDynamicChange.${status.dynamicKey}`,
      status?.isOnChange,
    )
  }
  if (designCode) {
    objectPath.set(isUiChanged, 'isCheckingDesignCode', designCode)
    setIsUiChanged(isUiChanged)
    checkConditionList(designContext, conditionData, uiKey, status)
  } else {
    checkConditionList(designContext, conditionData, uiKey, status)
  }
  if (status.isCloseModal) {
    let formData = data
    if (objectPath.get(formData, `@condition.${status.modalId}`)) {
      objectPath.del(formData, `@condition.${status.modalId}`)
      setFormData(formData)
    }
  }
}

export const onUpdateCondition = (
  props = {},
  designContext,
  uiKey,
  data,
  status = {},
) => {
  let {
    isDebounce = false,
    delayTime = 500,
    debounceName = 'on-check-condition',
  } = status

  let { config } = props
  let { isUiChanged, setIsUiChanged } = designContext
  let formComponentName = config?.formComponent?.name
  let isFormComponent = config?.isFormComponent
  let tabForm = objectPath.get(isUiChanged, ['tabForm', formComponentName])
  let formComponentCondition
  if (tabForm) {
    let { jsonForm } = tabForm
    formComponentCondition = objectPath.get(jsonForm, 'conditions') || []
  }

  if (isDebounce) {
    debounceCallback(
      onCheckCondition,
      [
        designContext,
        data,
        uiKey,
        {
          ...status,
          tabForm,
          isFormComponent,
          formComponentName,
          formComponentCondition,
        },
      ],
      debounceName,
      delayTime,
    )
  } else {
    onCheckCondition(designContext, data, uiKey, {
      ...status,
      tabForm,
      isFormComponent,
      formComponentName,
      formComponentCondition,
    })
  }
}

export const onCheckProviderCondition = (
  designContext,
  providerLocked,
  isLockWidget,
) => {
  let { elementAction, setElementAction, jsonForm } = designContext
  let { conditions = [] } = jsonForm
  let formData = jsonForm.formData
  let filterProvider = conditions.filter(({ isProvider }) => isProvider)
  if (isLockWidget) {
    objectPath.set(elementAction, `isLockedEdit`, isLockWidget)
  } else {
    filterProvider.forEach(
      ({ conditions = [], actions = [], connector = [] }) => {
        let conditionCheck = []
        let isPass
        conditions.forEach(({ name, state }, index) => {
          if (!providerLocked[name]) return
          let conditionValue = objectPath.get(providerLocked, [name, state])
          if (state === 'isLocked') {
            objectPath.set(elementAction, `isLockedEdit`, conditionValue)
          } else {
            objectPath.set(elementAction, state, conditionValue)
          }
          conditionCheck.push(conditionValue)
          if (objectPath.has(connector, index)) {
            let connectorValue = objectPath.get(connector, index)
            conditionCheck.push(connectorValue)
          }
        })
        if (conditionCheck.includes('and') && conditionCheck.includes('or')) {
          isPass = checkFalseFromAndOr(actions, isPass, '', false)
        } else if (conditionCheck.includes('and')) {
          if (conditionCheck.includes(false)) {
            isPass = false
          } else {
            isPass = true
          }
        } else if (conditionCheck.includes('or')) {
          if (conditionCheck.includes(true)) {
            isPass = true
          } else {
            isPass = false
          }
        } else {
          if (conditionCheck.includes(true)) {
            isPass = true
          } else {
            isPass = false
          }
        }
        if (isPass) {
          elementAction = runConditionActions({
            actions,
            formData,
            elementAction,
            designContext,
            status: {},
            isPass,
          })
        }
      },
    )
  }
  setElementAction(elementAction)
}

const onSet = (action, formData = {}, elementAction, designContext, status) => {
  let {
    getCurrentUi,
    setFieldFormData,
    setFieldSearchData,
    user,
    isUiChanged,
    urlParams,
  } = designContext
  let {
    key,
    isSet,
    valueType,
    valueTo,
    valueKey,
    replaceTo,
    customValue,
    formComponentName,
  } = action
  let { value } = isSet
  let { isPage, rowData = {}, isDynamic } = status
  let resultValue
  let ui = getCurrentUi(key)

  if (valueType === 'today') {
    if (ui.tagname === 'date') {
      resultValue = dayjs().format('YYYY-MM-DD')
    } else if (ui.tagname === 'time') {
      resultValue = dayjs().format('HH:mm:ss')
    } else if (ui.tagname === 'dateTime') {
      resultValue = getDateTimeFormValue(new Date(), ui?.timestamp)
    }
  } else if (valueType === 'defaultValue') {
    if (ui.tagname === 'date') {
      const defaultDate = {
        ...DEFAULT_DATE,
        custom: ui.defaultValue,
      }
      resultValue = defaultDate[ui.defaultType]
    } else if (ui.tagname === 'time') {
      const defaultTime = {
        ...DEFAULT_TIME,
        custom: ui.defaultValue,
      }
      resultValue = defaultTime[ui.defaultType]
    } else if (ui.tagname === 'dateTime') {
      const defaultDateTime = {
        ...DEFAULT_DATETIME,
        current: getDateTimeFormValue(new Date(), ui?.timestamp),
        custom: getDateTimeFormValue(ui.defaultValue, ui?.timestamp),
      }
      resultValue = defaultDateTime[ui.defaultType]
    } else {
      resultValue = ui.defaultValue
    }
  } else if (valueType === 'perspective') {
    resultValue = objectPath.get(urlParams, 0)
  } else if (valueType === 'userId') {
    resultValue = user.userId
  } else if (valueType === 'employeeCode') {
    resultValue = user.employeeCode
  } else if (valueType === 'comingTable') {
    let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')
    if (isWaitUpsert?.key === key) {
      let waitingTable = objectPath.get(isWaitUpsert, 'table')
      resultValue = waitingTable
    }
  } else if (valueType === 'rowTable') {
    let { field } = valueTo
    resultValue = objectPath.get(rowData, field)
  } else if (valueType === 'replaceValue') {
    let { field, groupType, key } = replaceTo
    let valueReplace
    if (groupType === 'queryParam') {
      let datasourceValue = objectPath.get(designContext, '@datasource')
      valueReplace = objectPath.get(datasourceValue, `${groupType}.${key}`)
    } else {
      valueReplace = objectPath.get(formData, key)
    }
    let oldString = objectPath.get(value, action.key)
    if (oldString && valueReplace) {
      resultValue = oldString.replace(`@${replaceTo.key}`, valueReplace)
    } else {
      resultValue = oldString.replace(`@${replaceTo.key}`, '')
    }
  } else if (valueType === 'custom') {
    resultValue = customValue
  } else {
    resultValue = objectPath.get(value, key)
  }

  if (isDynamic) {
    let { dynamicName, dynamicKey, dynamicIndex } = status
    setFieldFormData(`${dynamicKey}.${dynamicIndex}.${valueKey}`, resultValue)
    setFieldFormData(`${dynamicName}.${dynamicIndex}.${valueKey}`, resultValue)
  } else {
    if (isPage === 1) {
      setFieldSearchData(ui, key, resultValue)
      setFieldSearchData(ui, ui.name, resultValue)
      setFieldFormData(key, resultValue)
      setFieldFormData(ui.name, resultValue)
    } else {
      setFieldFormData(key, resultValue)
      setFieldFormData(ui.name, resultValue)
    }
    objectPath.set(formData, key, resultValue)
    objectPath.set(formData, ui.name, resultValue)
    formData = onCheckCondition(designContext, formData, key, {
      isDebounce: false,
    })
  }
  return { formData, elementAction }
}

const onUpdateDynamicData = (
  action,
  formData = {},
  elementAction,
  designContext,
) => {
  const { dynamicKey } = action
  const actionSetting = action.updateDynamic || {}
  const { dynamicList = [] } = actionSetting

  let dynamicData = objectPath.get(formData, dynamicKey)
  if (!dynamicData) return { formData, elementAction }
  dynamicData.map((rowValue) => {
    dynamicList.forEach(({ action, condition }) => {
      let isPass
      if (!condition || !action) return
      let { compareType, valueType } = condition
      let conditionName = condition?.field?.value
      if (!conditionName) return
      let conditionValue = objectPath.get(rowValue, conditionName)
      let compareValue
      if (valueType === 'custom') {
        compareValue = condition.value
      }
      isPass = compareTwoValue(compareType, conditionValue, compareValue)
      if (!isPass) return
      let actionName = action?.field?.value
      if (!actionName) return
      let actionValue
      if (valueType === 'custom') {
        actionValue = action.customValue
      }
      objectPath.set(rowValue, actionName, actionValue)
    })
  })
  return { formData, elementAction }
}

const onColorLabel = (
  { key, labelColor, warningColor },
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  let { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, `isChangeColor.${key}`, {
    labelColor,
    warningColor,
  })

  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onCalculate = (
  action,
  formValue,
  elementAction,
  designContext,
  status,
) => {
  let {
    setFieldFormData,
    setFieldSearchData,
    isUiChanged,
    setIsUiChanged,
    jsonForm,
  } = designContext
  let { isCalculate } = action
  let { isPage, isDynamic, dynamicIndex, dynamicName, dynamicKey } = status
  let { calculate, formula, result } = isCalculate
  let { data, link } = formula.find(({ key }) => key === calculate) || {}
  let ui = getCurrentUi(result.key, jsonForm.schema, jsonForm.uischema)

  let resultValue = updateCalculate(data, link, formValue, jsonForm.formData)

  let formData = jsonForm.formData
  let resultKey = result.key
  let resultName = result.name
  if (isDynamic && result.isDynamic) {
    resultKey = `${dynamicName}.${dynamicIndex}.${result.key}`
    resultName = `${dynamicName}.${dynamicIndex}.${result.name}`
  }
  objectPath.set(formData, resultKey, resultValue)
  objectPath.set(formData, resultName, resultValue)
  if (isPage === 1) {
    setFieldSearchData(ui, resultKey, resultValue)
    setFieldSearchData(ui, resultName, resultValue)
  } else {
    setFieldFormData(resultKey, resultValue)
    setFieldFormData(resultName, resultValue)
  }
  onUpdateCondition({}, designContext, result.key, formData, {
    isDebounce: false,
  })
  objectPath.set(isUiChanged, resultKey, { isRender: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onFetchOption = (
  action,
  formData,
  elementAction,
  designContext,
  status,
  uiKey,
) => {
  const { key } = action
  if (!key) return { formData, elementAction }

  const { formComponentName, ...fetchStatus } = createFetchOptionStatus(
    status,
    uiKey,
  )
  const { isUiChanged, setIsUiChanged } = designContext
  let updateOption = objectPath.get(elementAction, ['updateOption']) || []
  if (!Array.isArray(updateOption)) {
    updateOption = []
  }
  if (!updateOption.includes(key)) {
    updateOption.push(key)
    objectPath.set(elementAction, ['updateOption'], updateOption)
  }
  let isFetchOption = {
    key,
    forceFetch: Date.now(),
    ...fetchStatus,
  }

  if (
    !shouldUpdateFetchOptionStatus({
      isUiChanged,
      key,
      formComponentName,
      isFetchOption,
      fetchStatus,
    })
  ) {
    return { formData, elementAction }
  }

  objectPath.set(isUiChanged, ['optionUpdate', key], fetchStatus)

  if (formComponentName) {
    objectPath.set(isUiChanged, `${formComponentName}.${key}`, {
      isRender: true,
      isFetchOption,
    })
  } else {
    objectPath.set(isUiChanged, [key, 'isRender'], true)
    objectPath.set(isUiChanged, ['isFetchOption', key], isFetchOption)
  }

  setIsUiChanged({ ...isUiChanged })
  return { formData, elementAction }
}

const onFetchTable = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { key } = action
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, { isRender: true, isFetchTable: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onFetchComponent = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { key } = action
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, { isRender: true, isFetchComponent: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onUpdateCheckboxList = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  let { key } = action
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.push(elementAction, ['updateCheckboxList'], key)
  objectPath.set(isUiChanged, key, {
    isRender: true,
    updateCheckboxList: action,
    isDynamic,
    dynamicName,
    dynamicKey,
    dynamicIndex,
  })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onUpdateValue = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { jsonForm } = designContext
  const itemKey = action.key
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex, listItem } = status

  let { name, uiType, widget } = getCurrentUi(
    itemKey,
    jsonForm.schema,
    jsonForm.uischema,
  )
  if (uiType === 'widget') {
    let mapWidgetUi = objectPath.get(widget, 'schema.mapFormData')
    let filterFormWidget = Object.keys(mapWidgetUi).filter(
      (key) => mapWidgetUi[key],
    )
    filterFormWidget.forEach((key) => {
      let { name } = getCurrentUi(
        key,
        widget.jsonForm.schema,
        widget.jsonForm.uischema,
      )
      let value = listItem
        ? objectPath.get(listItem, name)
        : objectPath.get(formData, name)
      objectPath.set(formData, key, value)
      objectPath.set(formData, name, value)
      objectPath.set(isUiChanged, itemKey, { isRender: true })
    })
  } else {
    let value = listItem
      ? objectPath.get(listItem, name)
      : objectPath.get(formData, name)
    objectPath.set(formData, itemKey, value)
    objectPath.set(formData, name, value)
  }

  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, itemKey, {
    isRender: true,
    isDynamic,
    dynamicName,
    dynamicKey,
    dynamicIndex,
  })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onClearActive = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { key } = action
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, {
    isClearActive: true,
    isDynamic,
    dynamicName,
    dynamicKey,
    dynamicIndex,
  })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onClearActiveButton = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { setActiveButton } = designContext
  setActiveButton(null)
  return { formData, elementAction }
}

const updateTabActiveState = (
  designContext,
  tabKey,
  tabPanelKey,
  tabPanelUi,
  filterParam,
) => {
  const {
    tabParamActive,
    setTabParamActive,
    isUiChanged,
    setIsUiChanged,
    setTabActiveState,
  } = designContext
  if (setTabActiveState) {
    setTabActiveState({
      tabKey,
      tabPanelName: tabPanelUi.name,
      filterParam,
      shouldSetParam: true,
      tabState: {
        isActiveTab: true,
        tabKey: tabPanelKey,
        tabPanelUi,
      },
    })
    return
  }

  objectPath.set(tabParamActive, tabKey, filterParam)
  setTabParamActive(tabParamActive)
  objectPath.set(isUiChanged, ['activeTab', tabKey], tabPanelUi.name)
  objectPath.set(isUiChanged, tabKey, {
    isActiveTab: true,
    tabKey: tabPanelKey,
    tabPanelUi,
  })
  setIsUiChanged(isUiChanged)
}

const onClearActiveTab = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { key, targetTagname } = action
  const { getCurrentUi, isUiChanged } = designContext

  if (targetTagname !== 'tabPanel') return { formData, elementAction }

  let tabKeyList = cloneDeep(key.split('.')) || []
  tabKeyList.pop()
  let tabKey = tabKeyList.pop()
  if (!tabKey) return { formData, elementAction }

  let tabUi = getCurrentUi(tabKey)
  let defaultActiveKey = tabUi?.elements?.[0]?.key || ''
  if (!defaultActiveKey) return { formData, elementAction }

  let tabPanelUi = getCurrentUi(defaultActiveKey)
  let activeName = objectPath.get(isUiChanged, ['activeTab', tabKey])
  if (tabPanelUi?.name && activeName === tabPanelUi.name) {
    return { formData, elementAction }
  }

  let filterParam = (tabUi?.param || []).filter(
    (param) => param.tabKey === defaultActiveKey,
  )
  updateTabActiveState(
    designContext,
    tabKey,
    defaultActiveKey,
    tabPanelUi,
    filterParam,
  )
  return { formData, elementAction }
}

const onActiveButton = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { key } = action
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, {
    isActiveButton: true,
    isDynamic,
    dynamicName,
    dynamicKey,
    dynamicIndex,
  })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onActiveTab = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { key, name, activeTabField, targetTagname } = action
  const { jsonForm, isUiChanged } = designContext

  if (targetTagname === 'tabPanel') {
    let tabKeyList = cloneDeep(key.split('.')) || []
    tabKeyList.pop()
    let tabKey = tabKeyList.pop()
    if (!tabKey) return { formData, elementAction }
    let tabUi = getCurrentUi(tabKey, jsonForm.schema, jsonForm.uischema)
    let tabPanelUi = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    let defaultActiveKey = objectPath.get(isUiChanged, [tabKey, 'tabKey'])
    let activeName = objectPath.get(isUiChanged, ['activeTab', tabKey])
    if (
      defaultActiveKey === key ||
      (tabPanelUi?.name && activeName === tabPanelUi.name)
    ) {
      return { formData, elementAction }
    }
    let filterParam = (tabUi?.param || []).filter(
      (param) => param.tabKey === key,
    )
    updateTabActiveState(
      designContext,
      tabKey,
      key,
      tabPanelUi,
      filterParam,
    )
    return { formData, elementAction }
  }

  let tabUi = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
  let activeTabKey = activeTabField?.key
  if (activeTabKey) {
    let activeTabValue = objectPath.get(formData, activeTabKey)
    let { param = [] } = tabUi
    let foundActive = param.find(
      (param) => param.name === name && param.value === activeTabValue,
    )
    let tabKey = foundActive?.tabKey
    let defaultActiveKey = objectPath.get(isUiChanged, [key, 'tabKey'])
    if (defaultActiveKey === tabKey) return
    if (tabKey) {
      let filterParam = param.filter((param) => param.tabKey === tabKey)
      let tabPanelUi = getCurrentUi(tabKey, jsonForm.schema, jsonForm.uischema)
      updateTabActiveState(
        designContext,
        key,
        tabKey,
        tabPanelUi,
        filterParam,
      )
    }
  }
  return { formData, elementAction }
}

const onReset = (
  action,
  formData = {},
  elementAction,
  designContext,
  status,
) => {
  let {
    jsonForm,
    getCurrentUi,
    setFieldFormData,
    setFieldSearchData,
    setSearchData,
    setDesignerPropsData,
    setAllData,
    setIsResetData,
    isUiChanged,
    setIsUiChanged,
    setFormData,
    setDatasourceValue,
  } = designContext
  let { key, resetType, valueKey, formDataKey, groupType, isElement } = action
  const { isPage } = status
  const allFormGroup = getFormGroup(designContext)

  if (resetType === 'all' || valueKey === 'all') {
    let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
    formData = resetKey({
      keyList: getResetKeyList(jsonForm),
      formData: {},
      designContext,
    })
    clearFormStorage(jsonForm)
    objectPath.set(designerPropsData, `data`, {})
    objectPath.set(designerPropsData, `isReset`, true)
    setDesignerPropsData(designerPropsData)
    setAllData({})
    setSearchData && setSearchData({})
    setDatasourceValue && setDatasourceValue({})
    objectPath.set(isUiChanged, 'autocompleteOption', {})
    objectPath.set(isUiChanged, 'isResetAll', true)
    setIsUiChanged(isUiChanged)
    setIsResetData && setIsResetData(true)
  } else if (resetType === 'formGroup') {
    let form = allFormGroup.find(({ key }) => key === formDataKey) || {}
    let {
      isGroup,
      groupData = [],
      mapParam = {},
      mapArray = {},
    } = form
    let paramList = Object.keys(mapParam)
    paramList.forEach((key) => {
      let isArray = objectPath.get(mapArray, key)
      if (isArray) {
        let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
        if (ui.key === key) {
          let { elements = [] } = ui
          elements.forEach((mainElement) => {
            let name = mainElement.name
            if (mainElement.tagname === 'panel') {
              name = '@' + name
            }
            objectPath.del(formData, name)
            objectPath.del(formData, `@isCheckedAll.${name}`)
          })
        }
      }
    })
    if (isGroup) {
      groupData.forEach((group) => {
        formData = resetKey({
          keyList: getFormGroupResetKeyList(group),
          formData,
          designContext,
        })
      })
    } else {
      formData = resetKey({
        keyList: getFormGroupResetKeyList(form),
        formData,
        designContext,
      })
    }
  } else {
    if (isElement) {
      let ui = getCurrentUi(key)
      if (ui.key !== key) return
      objectPath.set(formData, key, undefined)
      objectPath.set(formData, ui.name, undefined)
      if (isPage === 1) {
        setFieldSearchData(ui, key, undefined)
        setFieldSearchData(ui, ui.name, undefined)
      } else {
        setFieldFormData(key, undefined)
        setFieldFormData(ui.name, undefined)
      }
      objectPath.set(isUiChanged, [key, 'isRender'], true)
      objectPath.set(isUiChanged, [key, 'isUnSelectRow'], status?.isUnSelectRow)
      setIsUiChanged(isUiChanged)
      if (ui.tagname === 'panelGroup') {
        let panelElements = ui.elements
        panelElements.forEach(({ name }) => {
          if (objectPath.has(formData, `@${name}`)) {
            setFieldFormData(`@${name}`, undefined)
          }
          if (objectPath.has(formData, `@isCheckedAll.@${name}`)) {
            setFieldFormData(`@isCheckedAll.@${name}`, undefined)
          }
        })
      } else if (ui.type === 'Group') {
        let mapKey = objectPath.get(jsonForm, 'schema.mapKey')
        Object.keys(mapKey).forEach((itemKey) => {
          let fullKey = mapKey[itemKey]
          let keyList = fullKey.split('.')
          if (keyList.includes(itemKey)) {
            let ui = getCurrentUi(itemKey, jsonForm.schema, jsonForm.uischema)
            if (ui.key === itemKey && ui.uiType === 'element') {
              setFieldSearchData(ui, itemKey, undefined)
              setFieldSearchData(ui, ui.name, undefined)
            }
          }
        })
      }
    } else if (groupType === 'response') {
      let [apiKey] = key.split('.')
      let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
      objectPath.del(formData, `@response.${key}`, undefined)
      objectPath.del(datasourceValue, apiKey)
      setFormData(formData)
      setDatasourceValue(datasourceValue)
    }
  }

  return { formData, elementAction }
}

export const checkHasShow = ({ designContext, uischema, config }) => {
  const { elementAction } = designContext
  const isShow = objectPath.get(elementAction, ['isShow'])
  if (config?.isDynamic) {
    let { dynamicKey, dynamicIndex } = config
    return objectPath.has(
      isShow,
      `${dynamicKey}.${dynamicIndex}.${uischema.key}`,
    )
  }
  return objectPath.has(isShow, [uischema.key])
}

export const checkIsShow = ({ designContext, uischema, config }) => {
  const { elementAction } = designContext
  const isShow = objectPath.get(elementAction, ['isShow'])
  if (config?.isDynamic) {
    let { dynamicKey, dynamicIndex } = config
    return objectPath.get(
      isShow,
      `${dynamicKey}.${dynamicIndex}.${uischema.key}`,
    )
  }
  return objectPath.get(isShow, [uischema.key])
}

const onShow = (action, formData, elementAction, designContext, status) => {
  elementAction = elementAction || {}
  const { key } = action
  const { isDynamic, dynamicKey, dynamicIndex } = status
  let itemKey
  if (isDynamic) {
    let showElement = objectPath.get(elementAction, ['isShow', dynamicKey])
    if (!Array.isArray(showElement)) {
      objectPath.set(elementAction, ['isShow', dynamicKey], [])
    }
    itemKey = ['isShow', dynamicKey, dynamicIndex, key].join('.')
  } else {
    itemKey = ['isShow', key].join('.')
  }
  objectPath.set(elementAction, itemKey, true)
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, { isRender: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onHide = (action, formData, elementAction, designContext, status) => {
  elementAction = elementAction || {}
  const { key } = action
  const { isDynamic, dynamicKey, dynamicIndex } = status
  let itemKey
  if (isDynamic) {
    let showElement = objectPath.get(elementAction, ['isShow', dynamicKey])
    if (!Array.isArray(showElement)) {
      objectPath.set(elementAction, ['isShow', dynamicKey], [])
    }
    itemKey = ['isShow', dynamicKey, dynamicIndex, key].join('.')
  } else {
    itemKey = ['isShow', key].join('.')
  }
  objectPath.set(elementAction, itemKey, false)
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, { isRender: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onLoading = ({ key }, formData, elementAction, designContext) => {
  objectPath.set(elementAction, ['isLoading', key], true)
  return { formData, elementAction }
}

const onDoneLoading = ({ key }, formData, elementAction, designContext) => {
  objectPath.set(elementAction, ['isLoading', key], false)
  return { formData, elementAction }
}

const onShowHideList = (
  action,
  formData,
  elementAction,
  designContext,
  uiKey,
) => {
  let { key } = action
  let { jsonForm } = designContext
  let compareKey = uiKey
  let compareData = objectPath.get(formData, compareKey)
  let compareUi = getCurrentUi(compareKey, jsonForm.schema, jsonForm.uischema)
  let actionUi = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
  let actionElements = cloneDeep(actionUi.elements)
  if (compareUi.isMulti) {
    let compareList
    if (compareUi.valueType === 'string') {
      compareList = compareData.split(compareUi.valueJoin || '') || []
    } else {
      compareList = compareData || []
    }
    if (!Array.isArray(compareList)) {
      compareList = [compareList]
    }
    if (actionElements && compareList) {
      actionElements.map((element) => {
        let foundOptionId = compareList.find(
          (value) => value === element.optionId,
        )
        let subElements = element.elements
        subElements.map((subElement) => {
          subElement.isShowUi = foundOptionId ? true : false
          return subElement
        })
        element.isShowUi = foundOptionId ? true : false
        element.elements = subElements
        objectPath.set(
          formData,
          `@${element.name}.@${compareUi.name}`,
          foundOptionId,
        )
        return element
      })
    }
  }
  updateUiSchema(key, 'elements', actionElements, jsonForm)
  objectPath.set(elementAction, ['isShowHideList', key], true)
  const { isUiChanged, setIsUiChanged } = designContext
  objectPath.set(isUiChanged, key, { isRender: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onEnable = ({ key }, formData, elementAction, designContext, status) => {
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  const { jsonForm, isUiChanged, setIsUiChanged } = designContext
  objectPath.set(elementAction, ['isEnable', key], true)
  elementAction = checkEnableContainer(key, true, jsonForm, elementAction)
  objectPath.set(isUiChanged, key, {
    isRender: true,
    isDynamic,
    dynamicName,
    dynamicKey,
    dynamicIndex,
  })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onDisable = (action, formData, elementAction, designContext, status) => {
  const { key } = action
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  const { jsonForm, isUiChanged, setIsUiChanged } = designContext
  objectPath.set(elementAction, ['isEnable', key], false)
  elementAction = checkEnableContainer(key, false, jsonForm, elementAction)
  objectPath.set(isUiChanged, key, {
    isRender: true,
    isDynamic,
    dynamicName,
    dynamicKey,
    dynamicIndex,
  })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onWarning = (action, formData, elementAction, isPass) => {
  let { key, isWarning } = action
  let warningMessage = objectPath.get(isWarning, 'warningMessage')
  let warningOnSubmit = objectPath.get(isWarning, 'warningOnSubmit')
  let showWarning = isPass ? warningMessage : ''
  if (warningOnSubmit) {
    objectPath.set(elementAction, ['warningOnSubmit', key], showWarning)
  } else {
    objectPath.set(elementAction, ['isWarning', key], showWarning)
  }
  return { formData, elementAction }
}

const onRequire = (action, formData, elementAction, isPass) => {
  let { key, isRequire } = action
  let requireMessage = objectPath.get(isRequire, 'requireMessage')
  let requireOnSubmit = objectPath.get(isRequire, 'requireOnSubmit')
  let showRequire = isPass ? requireMessage : ''
  if (requireOnSubmit) {
    objectPath.set(elementAction, ['requireOnSubmit', key], showRequire)
  } else {
    objectPath.set(elementAction, ['isRequire', key], showRequire)
  }
  return { formData, elementAction }
}

const onOpenWarningAlert = (action, formData, elementAction, designContext) => {
  const { isUiChanged, setIsUiChanged } = designContext
  let { titleValue, isWarning } = action
  let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  let html = []
  titleValue.map(({ title, fontBold, fontSize = '16px' }) => {
    if (!title) return ''
    let htmlString = []
    title.forEach(({ __isNew__, value }) => {
      if (__isNew__) {
        htmlString.push(value)
      } else {
        let itemValue = objectPath.get(datasourceValue, value)
        htmlString.push(itemValue)
      }
    })
    html.push(
      `<p class="mb-1 ${
        fontBold ? 'font-weight-bold' : ''
      }" style="font-size:${fontSize}">${htmlString.join('')}</p>`,
    )
  })
  let { icon, showWarningBack, showWarningReload } = isWarning
  let swalData = {
    icon: icon,
    html: html.join(''),
    confirmButtonText: `ปิด`,
  }
  if (showWarningBack) {
    swalData = {
      ...swalData,
      showCancelButton: true,
      cancelButtonText: 'กลับ',
      cancelButtonColor: '#fff',
    }
  }
  if (showWarningReload) {
    swalData = {
      ...swalData,
      showDenyButton: true,
      denyButtonText: 'Reload',
      denyButtonColor: '#fff',
    }
  }
  Swal.fire(swalData).then((result) => {
    if (result.isConfirmed) {
    } else if (result.isDenied) {
      window.location.reload()
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      objectPath.set(isUiChanged, ['closeAlert'], {
        isClose: true,
        alert: isWarning,
      })
      setIsUiChanged(isUiChanged)
    }
  })
  return { formData, elementAction }
}

export const onCallAPIAction = async (
  action,
  formData,
  elementAction,
  designContext,
  isPass,
  status = {},
) => {
  const requestVersion = getDesignerRequestVersion()
  if (!isPass) return
  let { key } = action
  const {
    isUiChanged,
    setIsUiChanged,
    isLoadingApi,
    setIsLoadingApi,
    jsonForm = {},
    designerData = {},
    urlParams,
    setFormData,
    setElementAction,
    setDatasourceValue,
    tabParamActive,
    user,
    searchData,
  } = designContext

  if (status.preloadedCallAPIKeys?.includes(key)) {
    return { formData, elementAction }
  }

  if (key && objectPath.get(isUiChanged, ['callAPI', key])) {
    return { formData, elementAction }
  }

  let tabForm = isUiChanged?.tabForm
  let apiGroup = createCallAPIActionList(jsonForm)
  if (tabForm) {
    Object.values(tabForm).forEach(({ jsonForm }) => {
      let tabApiGroup = createCallAPIActionList(jsonForm)
      apiGroup = [...apiGroup, ...tabApiGroup]
    })
  }
  let api = apiGroup.find((api) => api.key === key)

  let datasourceValue = objectPath.get(designContext, '@datasource')
  let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')
  let dataCheck = cloneDeep(formData)

  if (isWaitUpsert?.status) {
    let { isModalProp, isReplace, key, table = [], rowData = [] } = isWaitUpsert
    let tableFilterDelete = table.filter((rowData) => {
      if (objectPath.has(rowData, '@isDeleted')) {
        if (objectPath.get(rowData, '@isDeleted')) {
          return false
        }
      }
      return true
    })
    objectPath.set(dataCheck, `@isWaitUpsert.${key}`, rowData)
    objectPath.set(dataCheck, `@isConfirmUpsert.${key}`, table)
    if (isModalProp) {
      if (isReplace) {
        if (table.length === 0) {
          objectPath.set(dataCheck, key, rowData)
        } else {
          objectPath.set(dataCheck, key, tableFilterDelete)
        }
      } else {
        let comingTable = tableFilterDelete.concat(rowData)
        objectPath.set(dataCheck, key, comingTable)
      }
    } else {
      objectPath.set(dataCheck, key, tableFilterDelete)
    }
  }
  if (api) {
    let isFromCondition = true
    let data = {
      searchData,
      formData: dataCheck,
      urlParams,
      designerData,
      datasourceValue,
      tabParamActive,
      user,
    }
    let isPassCondition = checkConditionApi(
      api,
      urlParams,
      dataCheck,
      datasourceValue,
      tabParamActive,
    )
    if (!isPassCondition) return
    if (key) {
      objectPath.set(isUiChanged, ['callAPI', key], true)
    }
    try {
      let response
      if (api.method === 'get') {
        response = await getData(api, data, designContext, true)
      } else if (api.method === 'post') {
        response = await postData(api, data, designContext)
      } else if (api.method === 'put') {
        response = await putData(api, data, designContext)
      } else if (api.method === 'patch') {
        response = await patchData(api, data, designContext)
      } else if (api.method === 'delete') {
        response = await deleteData(api, data, designContext)
      }

      if (!response || !isDesignerRequestVersionActive(requestVersion)) return

      if (response) {
        let responseData = response?.data || {}
        formData = mapResponseData(
          api,
          responseData,
          formData,
          isWaitUpsert,
          jsonForm,
          designContext,
        )
        objectPath.set(formData, `@response.${api.key}`, responseData)

        if (api.dataKey) {
          let data = objectPath.get(responseData, api.dataKey)
          objectPath.set(datasourceValue, api.key, data)
          setDatasourceValue(datasourceValue)
        } else {
          objectPath.set(datasourceValue, api.key, responseData)
          setDatasourceValue(datasourceValue)
        }
        let checkStatus = {
          apiKey: api.key,
          response: { [api.key]: responseData },
          isCheckOnlyResponse: true,
        }
        onCheckCondition(designContext, formData, null, checkStatus)
        if (hasRules(jsonForm)) {
          formData = onCheckRules(designContext, formData, null, checkStatus)
        }
        let status = response.status
        if (status >= 200 && status < 300) {
          await fetchAfterSuccess({
            action: api,
            designContext,
            formData,
          })
        }
      }

      if (isWaitUpsert?.status) {
        let { key, isModalProp, isReplace, isApiTable } = isWaitUpsert
        let comingTable = objectPath.get(formData, `@isWaitUpsert.${key}`) || []
        let currentTable =
          objectPath.get(formData, `@isConfirmUpsert.${key}`) || []
        if (isModalProp) {
          if (!isReplace) {
            currentTable = currentTable.concat(comingTable)
          }
          objectPath.set(dataCheck, key, currentTable)
          objectPath.set(formData, `@isConfirmUpsert.${key}`, currentTable)
        }
        if (isApiTable) {
          objectPath.set(isUiChanged, 'isWaitFetchApiTable', true)
        }
      }
      setFormData(formData)
    } catch (error) {
      console.error('[ConditionCallAPI] call api error', error)
    } finally {
      if (!isDesignerRequestVersionActive(requestVersion)) return
      if (key) {
        objectPath.del(isUiChanged, ['callAPI', key])
      }
      let callAPI = objectPath.get(isUiChanged, ['callAPI']) || {}
      if (Object.keys(callAPI).length === 0) {
        objectPath.del(isUiChanged, ['isStartCallAPI'])
      }
      setIsUiChanged(isUiChanged)
      traceConditionAction(designContext, {
        type: 'action',
        trigger: 'response',
        actionKey: key,
        actionState: 'callAPI',
        status: 'done',
      })
    }
  }
  return { formData, elementAction }
}

const onCallTableAPIAction = async (
  action,
  formData,
  elementAction,
  designContext,
  isPass,
) => {
  if (!isPass) return
  const { isUiChanged, setIsUiChanged } = designContext
  let { isWaitUpsert = {} } = isUiChanged
  let { isAddRow, isEditRow, isReplace, rowData, key } = isWaitUpsert
  let newData = objectPath.get(formData, [key, 0])

  if (isAddRow) {
    objectPath.set(isUiChanged, [key], { isAddRow: true, newData })
  } else if (isEditRow) {
    objectPath.set(isUiChanged, [key], { isEditRow: true, newData })
  } else if (isReplace) {
    let rowEdit = objectPath.get(isUiChanged, '@rowEdit') || {}
    let newData = objectPath.get(rowData, [0]) || {}
    newData = { ...rowEdit, ...newData }
    objectPath.set(isUiChanged, [key], { isEditRow: true, newData })
  } else {
    objectPath.del(isUiChanged, [key])
  }
  objectPath.del(isUiChanged, 'isWaitUpsert')
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const getValueAge = (ageKey, age, action, designContext) => {
  let { jsonForm, setFieldFormData } = designContext
  if (objectPath.has(age, ageKey)) {
    let ageValue = objectPath.get(age, ageKey)
    let key = objectPath.get(action, [ageKey, 'key'])
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    setFieldFormData(key, ageValue)
    if (ui.key === key) {
      setFieldFormData(ui.name, ageValue)
    }
  }
}

const onDateToAge = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  let { key, dateToAge, ageFull, includeLastDay } = action
  let { jsonForm, setFieldFormData } = designContext

  let dateValue = objectPath.get(formData, key)
  let dateLimit = action?.dateLimit?.key
    ? objectPath.get(formData, action?.dateLimit?.key)
    : null
  let fieldType = objectPath.get(action, 'dateToAge.fieldType')
  let isMulti = fieldType === 'multi'
  let age = getDateAge(dateValue, dateToAge, {
    isMulti,
    dateLimit,
    includeLastDay,
  })
  if (isMulti) {
    getValueAge('ageYear', age, action, designContext)
    getValueAge('ageMonth', age, action, designContext)
    getValueAge('ageDay', age, action, designContext)
  } else {
    let ageKey = key
    if (ageFull?.key) {
      ageKey = ageFull.key
    }
    let ui = getCurrentUi(ageKey, jsonForm.schema, jsonForm.uischema)
    if (ui.key === ageKey) {
      setFieldFormData(ageKey, age)
      setFieldFormData(ui.name, age)
    }
  }
  return { formData, elementAction }
}

const onAgeToDate = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  let { key, ageToDate } = action
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  let { jsonForm, setFieldFormData } = designContext
  let age = objectPath.get(formData, key)
  let date = calAgeToDate(age)
  let dateKey = objectPath.get(ageToDate, 'key')
  let ui = getCurrentUi(dateKey, jsonForm.schema, jsonForm.uischema)
  if (ui.key === dateKey) {
    setFieldFormData(dateKey, date)
    setFieldFormData(ui.name, date)
  }
  return { formData, elementAction }
}
const onDateToText = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  let { key, formatSort, timeFormat, dateText } = action
  const { isDynamic, dynamicName, dynamicKey, dynamicIndex } = status
  let { jsonForm, setFieldFormData, formatDateTime } = designContext
  if (!formatDateTime) return { formData, elementAction }

  let dateValue = objectPath.get(formData, key)
  if (dateValue) {
    let dateList = []
    if (formatSort) {
      formatSort.forEach((format) => {
        let formatValue = objectPath.get(action, format)
        let value = formatDateTime(dateValue, formatValue)
        dateList.push(value)
      })
    }
    if (timeFormat) {
      let value = formatDateTime(dateValue, timeFormat)
      dateList.push(value)
    }
    let textValue = dateList.join(' ')
    let textKey = dateText.key
    let ui = getCurrentUi(textKey, jsonForm.schema, jsonForm.uischema)
    if (ui.key === textKey) {
      setFieldFormData(textKey, textValue)
      setFieldFormData(ui.name, textValue)
    }
  }
  return { formData, elementAction }
}

const clearCondition = (action, formData) => {
  let { formId } = action
  if (formId) {
    objectPath.set(formData, `@condition.${formId}`, {})
  }
  let condition = objectPath.get(formData, `@condition`)
  return formData
}

const getConditionModalProps = (modalAction = {}) => {
  let props = objectPath.get(modalAction, 'props')
  if (!props) return []
  if (Array.isArray(props)) return props
  if (typeof props === 'object') return Object.values(props)
  return []
}

const onOpenModal = (
  action,
  formData,
  elementAction,
  designContext,
  status,
  isPass,
) => {
  const {
    jsonForm,
    searchData,
    setOpenModalCondition,
    setDesignerPropsData,
    setOpenModal,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  let modalAction = objectPath.get(action, 'openModal.modal') || {}
  let formCode = modalAction.formCode
  let modalKey = action.key || formCode
  let isOpenModal =
    objectPath.get(elementAction, ['openModal', action.key]) ||
    objectPath.get(elementAction, ['openModal', modalKey])
  let isSameModalRender =
    objectPath.get(isUiChanged, 'isModalRender') === formCode
  let isSameModalOpen =
    isPass &&
    isSameModalRender &&
    objectPath.get(isUiChanged, 'modal.isOpenModal')

  if (isSameModalOpen || (isPass && isOpenModal && isSameModalRender)) {
    return { formData, elementAction }
  }

  objectPath.set(elementAction, ['openModal', modalKey], isPass)

  let { isCloseModal } = status
  if (isCloseModal) {
    objectPath.set(isUiChanged, 'modal', {})
    setIsUiChanged(isUiChanged)
    return { formData, elementAction }
  }
  if (status?.isOpenModal && !isPass) {
    return { formData, elementAction }
  }
  if (isPass) {
    formData = clearCondition(action, formData)
    objectPath.set(designerPropsData, 'modal', modalAction.formCode)
    designerPropsData = createDesignerData(
      designerPropsData,
      searchData,
      formData,
      getConditionModalProps(modalAction),
    )
    setDesignerPropsData(designerPropsData)
    if (action.key) {
      setOpenModal(action.key)
      let ui = getCurrentUi(action.key, jsonForm.schema, jsonForm.uischema)
      objectPath.set(isUiChanged, 'modal', {
        key: isPass,
        ui,
        modalAction,
        isOpenModal: isPass,
        isOpening: false,
        designerPropsData,
        isHasUi: true,
      })
    } else {
      setOpenModal(formCode)
      objectPath.set(isUiChanged, 'modal', {
        key: isPass,
        ui: { key: formCode },
        modalAction,
        isOpenModal: isPass,
        isOpening: false,
        designerPropsData,
        isHasUi: false,
      })
    }
    objectPath.set(isUiChanged, 'isModalRender', formCode)
  } else {
    objectPath.set(isUiChanged, 'modal', {})
  }

  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onOpenSuccessModal = (
  action,
  formData,
  elementAction,
  designContext,
  isPass,
) => {
  const { isUiChanged, setIsUiChanged } = designContext
  let modalAction = objectPath.get(action, 'successModal.modal') || {}
  if (isPass) {
    objectPath.set(isUiChanged, 'successModal', {
      isOpen: true,
      modalAction,
    })
  } else {
    objectPath.set(isUiChanged, ['successModal', 'isOpen'], false)
  }
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onReload = (formData, elementAction) => {
  window.location.reload()
  return { formData, elementAction }
}

const onShowLoadUI = (action, formData, elementAction, designContext) => {
  let { isUiChanged, setIsUiChanged } = designContext
  if (action) {
    let { key } = action
    objectPath.set(isUiChanged, ['isStartCallAPI', key], true)
  } else {
    objectPath.set(isUiChanged, ['isStartCallAPI', 'layout'], true)
  }
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const onCopy = (action, formData, elementAction, designContext, status) => {
  const {
    setFieldFormData,
    jsonForm,
    isUiChanged,
    setIsUiChanged,
    urlParams,
    setFormData,
  } = designContext
  let datasources = objectPath.get(designContext, '@datasource') || {}
  let data = {
    formData,
    urlParams,
    datasources: datasources,
  }

  let { isDynamic, isMainDynamic, dynamicName, dynamicKey, dynamicIndex } =
    status
  let copyFromKey = action?.copyFrom?.key
  let copyFromName = action?.copyFrom?.valueKey
  let copyToKey = action?.copyTo?.key

  let copyFromLayout = action?.copyFrom?.layout
  let copyToLayout = action?.copyTo?.layout

  if (copyFromKey && copyToKey) {
    let copyToUi = getCurrentUi(copyToKey, jsonForm.schema, jsonForm.uischema)
    let copyToUiName = copyToUi?.name
    if (isMainDynamic) {
      let resultValue = objectPath.get(
        formData,
        `${dynamicName}.${dynamicIndex}.${copyFromName}`,
      )
      setFieldFormData(
        `${dynamicKey}.${dynamicIndex}.${copyToUiName}`,
        resultValue,
      )
      setFieldFormData(
        `${dynamicName}.${dynamicIndex}.${copyToKey}`,
        resultValue,
      )
    } else if (isDynamic) {
      let resultValue = objectPath.get(formData, copyFromName)

      setFieldFormData(
        `${dynamicKey}.${dynamicIndex}.${copyToUiName}`,
        resultValue,
      )
      setFieldFormData(
        `${dynamicName}.${dynamicIndex}.${copyToKey}`,
        resultValue,
      )
    } else {
      let copyFromValue = getCompareValue(
        copyFromKey,
        copyFromName,
        copyFromLayout,
        data,
      )
      objectPath.set(formData, copyToKey, copyFromValue)
      objectPath.set(formData, copyToUiName, copyFromValue)
      setFormData(formData)
      objectPath.set(isUiChanged, copyToKey, { isRender: true })
      setIsUiChanged(isUiChanged)
    }
  }
  return { formData, elementAction }
}

const onGoToUrl = (action, formData, elementAction, designContext, isPass) => {
  if (!isPass) return { formData, elementAction }
  let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  let { state } = action
  let {
    url = '',
    linkType = '',
    pagePath = '',
    pathParams = [],
    queryParams = [],
    openNewTab = false,
    perspectiveRoute = '',
  } = objectPath.get(action, state)
  if (['internal', 'page', 'form', 'print', 'url'].includes(linkType)) {
    url = pagePath
  } else if (linkType === 'api') {
    let { apiType, service = '', endpoint = '', api = '' } =
      objectPath.get(action, state)
    url = apiType === 'internal' ? service + endpoint : api
  }
  if (perspectiveRoute && linkType !== 'url') {
    let perspective = perspectiveRoute
    if (perspective.startsWith('/#')) {
      perspective = perspective.replace('/#', '')
    }
    if (perspective.startsWith('/')) {
      url = `${perspective}${url}`
    } else {
      url = `/${perspective}${url}`
    }
  }
  let allParams = designContext?.urlParams || {}
  if (pathParams) {
    pathParams.forEach(({ param, propType, key, value, sourceKey }) => {
      let paramValue
      if (propType === 'datasource') {
        paramValue = objectPath.get(datasourceValue, `${sourceKey}.${key}`)
      } else if (propType === 'urlParam') {
        paramValue = objectPath.get(allParams, key)
      } else if (propType === 'input') {
        paramValue = objectPath.get(formData, key)
      } else {
        paramValue = value
      }
      url = url.replace(`:${param}`, paramValue)
    })
  }
  if (queryParams) {
    let queryString = []
    queryParams.forEach(({ param, propType, key, value, sourceKey }) => {
      let paramValue
      if (propType === 'datasource') {
        paramValue = objectPath.get(datasourceValue, `${sourceKey}.${key}`)
      } else if (propType === 'urlParam') {
        paramValue = objectPath.get(allParams, key)
      } else if (propType === 'input') {
        paramValue = objectPath.get(formData, key)
      } else {
        paramValue = value
      }
      if (paramValue !== null && paramValue !== undefined) {
        queryString.push(`${param}=${paramValue}`)
      }
    })
    if (queryString.length > 0) {
      let connect = url.indexOf('?') > -1 ? '&' : '?'
      url = url ? url + connect + queryString.join('&') : url
    }
  }
  let target = openNewTab ? '_blank' : '_self'
  if (linkType === 'api') {
    if (url.startsWith('/')) {
      window.open(ROUTE_API + url, target)
    } else {
      window.open(`${ROUTE_API}/` + url, target)
    }
  } else if (isExternalApiUrl(linkType, url)) {
    window.open(url, target)
  } else if (url.startsWith('http')) {
    window.open(url, target)
  } else if (url.startsWith('/')) {
    window.open(ROUTE_HIS + url, target)
  } else {
    window.open(`${ROUTE_HIS}/` + url, target)
  }
  return { formData, elementAction }
}

const onCheckSelectRowTable = (
  action,
  formData,
  elementAction,
  designContext,
  status,
) => {
  const { isUiChanged, setIsUiChanged, jsonForm, setFieldFormData } =
    designContext
  let { key, fieldMapColumnRefId } = action
  let { isSelectRow } = status
  let uischema = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
  if (uischema.key !== key) return { formData, elementAction }
  let tableData =
    objectPath.get(formData, uischema.name) ||
    objectPath.get(formData, uischema.key) ||
    []
  let columnRefId = uischema?.loadData?.columnRefId
  let fieldMapColumnRefName = fieldMapColumnRefId?.valueKey
  let fieldMapColumnRefKey = fieldMapColumnRefId?.key
  if (!fieldMapColumnRefKey) return { formData, elementAction }
  let fieldRefId =
    objectPath.get(formData, fieldMapColumnRefName) ||
    objectPath.get(formData, fieldMapColumnRefKey)

  if (!columnRefId || !fieldRefId) return { formData, elementAction }
  let rowData = tableData.find(
    (rowItem) => objectPath.get(rowItem, columnRefId) === fieldRefId,
  )

  if (isSelectRow && rowData) {
    setFieldFormData(`@rowSelected.${uischema.key}`, rowData)
  } else {
    setFieldFormData(`@rowSelected.${uischema.key}`, {})
  }
  objectPath.set(isUiChanged, uischema.key, { isRender: true })
  setIsUiChanged(isUiChanged)
  return { formData, elementAction }
}

const callAction = ({
  action,
  formData,
  elementAction,
  designContext,
  status,
  isPass,
  uiKey,
}) => {
  let { state } = action
  let isCall = isPass === true

  if (!isCall) return { formData, elementAction }

  switch (state) {
    case 'isShow':
      return onShow(action, formData, elementAction, designContext, status)
    case 'isHide':
      return onHide(action, formData, elementAction, designContext, status)
    case 'isShowHideList':
      return onShowHideList(
        action,
        formData,
        elementAction,
        designContext,
        uiKey,
      )
    case 'isEnable':
      return onEnable(action, formData, elementAction, designContext, status)
    case 'isDisable':
      return onDisable(action, formData, elementAction, designContext, status)
    case 'isSet':
      return onSet(action, formData, elementAction, designContext, status)
    case 'isReset':
      return onReset(action, formData, elementAction, designContext, status)
    case 'isColorLabel':
      return onColorLabel(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'isCalculate':
      return onCalculate(action, formData, elementAction, designContext, status)
    case 'updateOption':
      return onFetchOption(
        action,
        formData,
        elementAction,
        designContext,
        status,
        uiKey,
      )
    case 'fetchComponent':
      return onFetchComponent(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'fetchTable':
      return onFetchTable(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'updateCheckboxList':
      return onUpdateCheckboxList(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'updateValue':
      return onUpdateValue(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'activeButton':
      return onActiveButton(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'activeTab':
      return onActiveTab(action, formData, elementAction, designContext, status)
    case 'clearActive':
      return onClearActive(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'clearActiveButton':
      return onClearActiveButton(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'clearActiveTab':
      return onClearActiveTab(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'isWarning':
      if (action.warningType === 'alert') {
        return onOpenWarningAlert(
          action,
          formData,
          elementAction,
          designContext,
          isPass,
        )
      }
      return onWarning(action, formData, elementAction, isPass)
    case 'isRequire':
      return onRequire(action, formData, elementAction, isPass)
    case 'callAPI':
      return onCallAPIAction(
        action,
        formData,
        elementAction,
        designContext,
        isPass,
        status,
      )
    case 'callTableAPI':
      return onCallTableAPIAction(
        action,
        formData,
        elementAction,
        designContext,
        isPass,
      )
    case 'dateToAge':
      return onDateToAge(action, formData, elementAction, designContext, status)
    case 'dateToText':
      return onDateToText(
        action,
        formData,
        elementAction,
        designContext,
        status,
      )
    case 'ageToDate':
      return onAgeToDate(action, formData, elementAction, designContext, status)
    case 'openModal':
      return onOpenModal(
        action,
        formData,
        elementAction,
        designContext,
        status,
        isPass,
      )
    case 'goToPrint':
      return onGoToUrl(action, formData, elementAction, designContext, isPass)
    case 'goToUrl':
      return onGoToUrl(action, formData, elementAction, designContext, isPass)
    case 'isCopy':
      return onCopy(action, formData, elementAction, designContext, status)
    case 'isLoading':
      return onLoading(action, formData, elementAction, designContext, isPass)
    case 'isDoneLoading':
      return onDoneLoading(
        action,
        formData,
        elementAction,
        designContext,
        isPass,
      )
    case 'syncData':
      return onSyncData(action, formData, elementAction, designContext, status)
    case 'successModal':
      return onOpenSuccessModal(
        action,
        formData,
        elementAction,
        designContext,
        isPass,
      )
    case 'isReload':
      return onReload(formData, elementAction)
    case 'isShowLoadUI':
      return onShowLoadUI(action, formData, elementAction, designContext)

    case 'unselectRowTable':
      return onCheckSelectRowTable(
        action,
        formData,
        elementAction,
        designContext,
        {
          ...status,
          isSelectRow: false,
        },
      )
    case 'selectRowTable':
      return onCheckSelectRowTable(
        action,
        formData,
        elementAction,
        designContext,
        {
          ...status,
          isSelectRow: true,
        },
      )
    // case 'updateDynamic':
    //   return onUpdateDynamicData(
    //     action,
    //     formData,
    //     elementAction,
    //     designContext,
    //     status,
    //   )
    default:
      return { formData, elementAction }
  }
}
